import {
  collection,
  doc,
  getDoc,
  getDocFromCache,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  deleteField,
  increment,
  writeBatch,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
  documentId,
} from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { Post, PostCategory } from '../types';
import { mapDoc, paginatedQuery } from '@utils/firestore';
import { AppError } from '../utils/errorHandler';
import { PostSchema } from '../schemas/firestoreSchemas';
import * as ngeohash from 'ngeohash';
import { visibilityConstraint, type PostVisibilityScope } from '@utils/postVisibility';
import type { CreatePostData, UpdatePostData } from '../types/api';
import { createPrefixedLogger } from '../utils/logger';
import {
  MAP_POSTS_LIMIT,
  MAP_POSTS_WINDOW_DAYS,
  POSTS_PER_PAGE,
  FEED_LIVE_LIMIT,
} from '../constants/posts';

export type { CreatePostData, UpdatePostData } from '../types/api';

const log = createPrefixedLogger('api:posts');

// Fires at most once per session: live snapshots re-fire on every write to
// the window, so without this a pinned-at-cap map would spam the warning on
// every new post.
let mapCapWarningLogged = false;

// Firestore rules enforce post visibility, and list queries are denied
// outright unless their filters prove the read rule — so every list query
// here carries either a visibility constraint (via the caller's scope) or
// an authorId == own-uid constraint. See src/utils/postVisibility.ts.
export const postsApi = {
  // categoryFilter is optional so existing callers (none today reach across
  // surfaces, but the shape stays general) keep working unchanged. When set,
  // it's appended after the visibility constraint and before the sort —
  // matches the composite index in firestore.indexes.json
  // (category ASC, visibility ASC, createdAt DESC).
  async getFeed(
    scope: PostVisibilityScope,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
    pageSize = POSTS_PER_PAGE,
    categoryFilter?: PostCategory
  ): Promise<{ posts: Post[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    const result = await paginatedQuery(
      collection(db, COLLECTIONS.POSTS),
      [
        visibilityConstraint(scope),
        ...(categoryFilter ? [where('category', '==', categoryFilter)] : []),
        orderBy('createdAt', 'desc'),
      ],
      lastDoc,
      pageSize,
      PostSchema
    );
    return { posts: result.items, lastDoc: result.lastDoc };
  },

  // Rules only allow this shape when userId is the requester's own uid (the
  // author may read all their own posts, any visibility). The app never
  // lists another user's posts — there is no other-user profile screen.
  async getPostsByUser(
    userId: string,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
    pageSize = POSTS_PER_PAGE
  ): Promise<{ posts: Post[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    const result = await paginatedQuery(
      collection(db, COLLECTIONS.POSTS),
      [where('authorId', '==', userId), orderBy('createdAt', 'desc')],
      lastDoc,
      pageSize,
      PostSchema
    );
    return { posts: result.items, lastDoc: result.lastDoc };
  },

  async getPostsByStructure(
    structureId: string,
    scope: PostVisibilityScope,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
    pageSize = POSTS_PER_PAGE
  ): Promise<{ posts: Post[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    const result = await paginatedQuery(
      collection(db, COLLECTIONS.POSTS),
      [
        where('structureId', '==', structureId),
        visibilityConstraint(scope),
        orderBy('createdAt', 'desc'),
      ],
      lastDoc,
      pageSize,
      PostSchema
    );
    return { posts: result.items, lastDoc: result.lastDoc };
  },

  // The viewer's own posts on a structure, any visibility (allowed by the
  // authorId == uid branch of the read rule). Merged into structure threads
  // so authors see their private/insurance posts alongside the public list.
  async getOwnPostsByStructure(structureId: string, uid: string, max = 50): Promise<Post[]> {
    const q = query(
      collection(db, COLLECTIONS.POSTS),
      where('structureId', '==', structureId),
      where('authorId', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    const snap = await getDocs(q);
    const posts: Post[] = [];
    snap.forEach((d) => {
      const p = mapDoc(d, PostSchema);
      if (p) posts.push(p);
    });
    return posts;
  },

  async getPost(postId: string): Promise<Post | null> {
    const docRef = doc(db, COLLECTIONS.POSTS, postId);
    const snapshot = await getDoc(docRef);
    return mapDoc(snapshot, PostSchema);
  },

  // Live subscription for the map's observation layer. Pulls recent public
  // posts (bounded to MAP_POSTS_WINDOW_DAYS — old observations age off the
  // map but stay reachable in feed/threads/deep links); the consumer filters
  // out anything without a location and any structure-linked posts (those
  // are represented by structure pins). Public-only for every viewer — the
  // rules deny an unfiltered query, and the map is an ambient community
  // layer, so authors' own private posts intentionally don't pin here.
  subscribeRecentPosts(
    callback: (posts: Post[]) => void,
    onError?: (error: Error) => void,
    max = MAP_POSTS_LIMIT
  ): () => void {
    const windowStart = Timestamp.fromMillis(
      Date.now() - MAP_POSTS_WINDOW_DAYS * 24 * 60 * 60 * 1000
    );
    const q = query(
      collection(db, COLLECTIONS.POSTS),
      where('visibility', '==', 'public'),
      where('createdAt', '>=', windowStart),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    return onSnapshotWithRetry(
      q,
      (snap) => {
        if (snap.size >= MAP_POSTS_LIMIT && !mapCapWarningLogged) {
          mapCapWarningLogged = true;
          const msg =
            '[map-cap] map post query returned the full limit (' +
            MAP_POSTS_LIMIT +
            '); older posts are being clipped';
          console.warn(msg);
          log.warn(msg);
        }
        const posts: Post[] = [];
        snap.forEach((d) => {
          const p = mapDoc(d, PostSchema);
          if (p) posts.push(p);
        });
        callback(posts);
      },
      (error) => {
        onError?.(error);
      }
    );
  },

  // Live subscription for the feed's head window. Mirrors getFeed's
  // visibility-scoped query (same rules-required constraint) but as an
  // onSnapshot instead of getDocs, so posts created in other
  // sessions/devices surface without a manual refetch. Deliberately no
  // `createdAt >=` range filter: a doc whose serverTimestamp() hasn't
  // resolved yet on the server would sort past any fixed lower bound taken
  // at subscribe time, and mapDoc resolves pending timestamps to a local
  // estimate (`serverTimestamps: 'estimate'`) rather than dropping them, so
  // the doc is included with an estimated createdAt instead of being missed.
  // categoryFilter mirrors getFeed's — see the comment there.
  subscribeFeed(
    scope: PostVisibilityScope,
    callback: (posts: Post[]) => void,
    onError?: (error: Error) => void,
    max = FEED_LIVE_LIMIT,
    categoryFilter?: PostCategory
  ): () => void {
    const q = query(
      collection(db, COLLECTIONS.POSTS),
      visibilityConstraint(scope),
      ...(categoryFilter ? [where('category', '==', categoryFilter)] : []),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    return onSnapshotWithRetry(
      q,
      (snap) => {
        const posts: Post[] = [];
        snap.forEach((d) => {
          const p = mapDoc(d, PostSchema);
          if (p) posts.push(p);
        });
        callback(posts);
      },
      (error) => {
        onError?.(error);
      }
    );
  },

  // postId is optional and, when passed, must be pre-generated by the
  // caller (see app/post/create.tsx) — it doubles as both the Firestore doc
  // id and the Storage path segment for the post's image. Passing it makes
  // the call idempotent: replaying with the same postId (e.g. an outbox
  // retry after a lost ACK) is safe to call again.
  //
  // opts.replay marks a call that may be re-running a create that already
  // committed — today only the outbox drain. It is what turns the existence
  // guard below on; a first attempt always carries a freshly generated id,
  // so the guard could only ever report "absent" and the round trip is
  // wasted. See the guard for why running it unconditionally was a bug.
  async createPost(
    data: CreatePostData,
    postId?: string,
    opts?: { replay?: boolean }
  ): Promise<Post> {
    const isAnonymous = data.isAnonymous ?? false;
    const locationHidden = data.locationHidden ?? false;

    // When the post is anonymous, mask author identity in the stored doc —
    // posts are world-readable, so "anonymous in the UI" isn't enough. When
    // location is hidden, drop the coords entirely rather than trusting the
    // client to not display them.
    const location =
      locationHidden || !data.location
        ? null
        : {
            ...data.location,
            geohash: ngeohash.encode(data.location.latitude, data.location.longitude, 7),
          };

    const rawPostData = {
      ...data,
      authorName: isAnonymous ? '' : data.authorName,
      authorAvatarUrl: isAnonymous ? undefined : data.authorAvatarUrl,
      location,
      locationHidden,
      isAnonymous,
      likesCount: 0,
      commentsCount: 0,
      createdAt: serverTimestamp(),
    };
    // Firestore rejects undefined values. Strip them from the top level
    // (e.g. authorAvatarUrl or thumbnailPath may legitimately be absent).
    const postData = Object.fromEntries(
      Object.entries(rawPostData).filter(([, v]) => v !== undefined)
    ) as typeof rawPostData;

    const docRef = postId
      ? doc(db, COLLECTIONS.POSTS, postId)
      : doc(collection(db, COLLECTIONS.POSTS));

    // Replay guard: only meaningful on a replay (see opts.replay). If the
    // doc already exists, a prior call with this same postId already
    // committed the batch below — the post doc and both counters land
    // atomically together, so doc existence alone proves the counters were
    // already applied. Re-running the batch would double-count, so skip
    // straight to the re-fetch.
    //
    // A missing post reads back as permission-denied, NOT as an empty
    // snapshot: the read rule is `canReadPost(resource.data)` and on a get
    // of an absent doc `resource` is null, so the dereference errors and
    // Firestore denies. Running this guard on every create (which is what
    // happened until the fix, because create.tsx always supplies a postId)
    // therefore made every first post attempt fail with
    // "Missing or insufficient permissions" before the batch was ever
    // reached. Treat denied as "not written yet": canReadPost's author
    // disjunct makes a post unconditionally readable by its own author, so
    // denied on our own postId can only mean the doc is absent.
    //
    // Stays server-first deliberately — see the re-fetch comment below.
    let alreadyExists = false;
    if (postId && opts?.replay) {
      try {
        alreadyExists = (await getDoc(docRef)).exists();
      } catch (err) {
        if ((err as { code?: string })?.code !== 'permission-denied') throw err;
        log.warn('existence guard denied, treating post as not yet written:', postId);
      }
    }

    if (!alreadyExists) {
      const batch = writeBatch(db);
      batch.set(docRef, postData);
      // Update structure posts count if linked
      if (data.structureId) {
        const structureRef = doc(db, COLLECTIONS.STRUCTURES, data.structureId);
        batch.update(structureRef, {
          postsCount: increment(1),
          updatedAt: serverTimestamp(),
        });
      }
      // Update user posts count
      const userRef = doc(db, COLLECTIONS.USERS, data.authorId);
      batch.update(userRef, {
        postsCount: increment(1),
      });
      await batch.commit();
    }

    // Re-fetch so the returned Post has a usable Timestamp (serverTimestamp
    // above is a FieldValue placeholder) and so schema validation catches
    // drift between this write and PostSchema. Cache first: our own commit
    // is always in the local cache, and mapDoc reads with
    // serverTimestamps:'estimate', so the pending createdAt passes the
    // schema — whereas a server-first getDoc can hang for minutes after an
    // offline window while the stream re-establishes (seen in the offline
    // audit), long after the write itself landed. The live feed listener
    // delivers the authoritative doc moments later either way. NOTE: the
    // existence guard above must stay server-first — on native the
    // Firestore cache is memory-only while the outbox survives restarts,
    // so a cache-first guard would miss a prior committed attempt and
    // double-count the counters.
    let snapshot;
    try {
      snapshot = await getDocFromCache(docRef);
    } catch {
      // Cache miss should be impossible right after our own commit, but
      // getDocFromCache throws when persistence is unavailable — fall back
      // to the server read rather than failing the create.
      snapshot = await getDoc(docRef);
    }
    const created = mapDoc(snapshot, PostSchema);
    if (!created) {
      throw new AppError('Post was created but could not be re-read.', 'FIRESTORE_ERROR');
    }
    return created;
  },

  async updatePost(postId: string, data: UpdatePostData): Promise<void> {
    const docRef = doc(db, COLLECTIONS.POSTS, postId);
    // Hiding the location on an existing post has to null the coords too —
    // setting only `locationHidden: true` would leave the lat/lng readable
    // to world-readable SDK consumers.
    const payload: Record<string, unknown> = { ...data, updatedAt: serverTimestamp() };
    // Location edits (author fixing a misplaced pin) need a fresh geohash,
    // mirroring createPost — otherwise the stored geohash goes stale and
    // range queries against it drift from the displayed pin.
    if (data.location) {
      payload.location = {
        ...data.location,
        geohash: ngeohash.encode(data.location.latitude, data.location.longitude, 7),
      };
    }
    if (data.locationHidden === true) payload.location = null;
    // null means "remove the checklist link" — stored docs use field absence,
    // not null, so the read-side schema stays z.string().optional().
    if (data.address === null) payload.address = deleteField();
    if (data.title === null) payload.title = deleteField();
    if (data.checklistItemId === null) payload.checklistItemId = deleteField();
    if (data.checklistItemLabel === null) payload.checklistItemLabel = deleteField();
    // Enforce the anonymity mask at the API layer too — mirrors createPost.
    // Posts are world-readable, so a client that merely hid the name in the
    // UI wouldn't be enough; the stored doc itself must not carry identity
    // when isAnonymous is true. This also satisfies the rules' update-block
    // anonymity invariant (firestore.rules), so a caller that forgot to mask
    // client-side still writes a compliant doc.
    if (data.isAnonymous === true) {
      payload.authorName = '';
      payload.authorAvatarUrl = deleteField();
    } else if (data.authorAvatarUrl === null) {
      payload.authorAvatarUrl = deleteField();
    }
    await updateDoc(docRef, payload);
  },

  async deletePost(postId: string, authorId: string, structureId?: string): Promise<void> {
    // One batch, for the same reason createPost uses one: the delete and both
    // decrements land together or not at all. As three separate awaits, a
    // closed tab or a dropped connection after the first call left the post
    // gone and users/{authorId}.postsCount permanently high — which is what
    // the nightly posts-count invariant caught on 2026-08-01
    // (scripts/audit-data-invariants.ts, check 4).
    const batch = writeBatch(db);
    batch.delete(doc(db, COLLECTIONS.POSTS, postId));

    // Update structure posts count if linked
    if (structureId) {
      batch.update(doc(db, COLLECTIONS.STRUCTURES, structureId), {
        postsCount: increment(-1),
        updatedAt: serverTimestamp(),
      });
    }

    // Update user posts count
    batch.update(doc(db, COLLECTIONS.USERS, authorId), {
      postsCount: increment(-1),
    });

    await batch.commit();
  },

  async likePost(postId: string, userId: string): Promise<void> {
    const likeRef = doc(db, COLLECTIONS.POSTS, postId, 'likes', userId);
    const mirrorRef = doc(db, COLLECTIONS.USERS, userId, 'likedPosts', postId);
    const postRef = doc(db, COLLECTIONS.POSTS, postId);

    // Query covers both the new `likes/{userId}` layout and any legacy
    // random-ID docs written by earlier versions (the old code path used
    // addDoc, so web-created likes live under auto-generated IDs and the
    // doc-by-id existence check would miss them — causing a second like to
    // double-count). The mirror doc is checked too: rules keep it immutable
    // (update: false), so a blind setDoc on an existing mirror is DENIED —
    // which used to fail the whole like while the like doc had already
    // landed, leaving an orphaned like and a reverted button.
    const [existing, mirrorSnap] = await Promise.all([
      getDocs(
        query(collection(db, COLLECTIONS.POSTS, postId, 'likes'), where('userId', '==', userId))
      ),
      getDoc(mirrorRef),
    ]);
    if (!existing.empty) {
      // Like exists but the mirror may predate the dual-write (stale
      // bundle) — heal it so like-state checks stay truthful.
      if (!mirrorSnap.exists()) await setDoc(mirrorRef, { createdAt: serverTimestamp() });
      return;
    }

    // One atomic batch: a denial of any write applies none of them, so the
    // like doc, its per-user mirror, and the counter can never desync.
    const batch = writeBatch(db);
    batch.set(likeRef, {
      userId,
      createdAt: serverTimestamp(),
    });
    // Per-user mirror keyed by post id; batchCheckLikes reads only this.
    if (!mirrorSnap.exists()) batch.set(mirrorRef, { createdAt: serverTimestamp() });
    batch.update(postRef, {
      likesCount: increment(1),
    });
    await batch.commit();
  },

  async unlikePost(postId: string, userId: string): Promise<void> {
    const likesQuery = query(
      collection(db, COLLECTIONS.POSTS, postId, 'likes'),
      where('userId', '==', userId)
    );

    const likesSnapshot = await getDocs(likesQuery);
    if (likesSnapshot.empty) {
      return; // Not liked
    }

    const postRef = doc(db, COLLECTIONS.POSTS, postId);

    // Same atomicity as likePost: deletes + counter in one batch.
    const batch = writeBatch(db);
    likesSnapshot.docs.forEach((d) => batch.delete(d.ref));
    batch.delete(doc(db, COLLECTIONS.USERS, userId, 'likedPosts', postId));
    batch.update(postRef, {
      likesCount: increment(-1),
    });
    await batch.commit();
  },

  async hasLiked(postId: string, userId: string): Promise<boolean> {
    const likesQuery = query(
      collection(db, COLLECTIONS.POSTS, postId, 'likes'),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(likesQuery);
    return !snapshot.empty;
  },

  // Like-state for a page of posts via the viewer's own likedPosts mirror:
  // documentId-in queries (≤30 ids each) bill only matching docs (min 1 per
  // query) instead of the old shape's one query per post.
  async batchCheckLikes(postIds: string[], userId: string): Promise<Set<string>> {
    const likedIds = new Set<string>();
    const IN_LIMIT = 30;
    const mirror = collection(db, COLLECTIONS.USERS, userId, 'likedPosts');
    const chunks: string[][] = [];
    for (let i = 0; i < postIds.length; i += IN_LIMIT) {
      chunks.push(postIds.slice(i, i + IN_LIMIT));
    }
    const results = await Promise.all(
      chunks.map((ids) => getDocs(query(mirror, where(documentId(), 'in', ids))))
    );
    for (const snap of results) {
      snap.forEach((d) => likedIds.add(d.id));
    }
    return likedIds;
  },
};
